/**
 * ElevationProfile — Terrain cross-section panel
 *
 * Renders a canvas-based elevation chart between two map points.
 * Fixed-position bottom panel, 200px tall, full width.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { fetchElevationProfile } from '../utils/elevationService.js'

/**
 * Draw the elevation chart onto a canvas element.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {{distance: number, elevation: number|null}[]} data
 */
function drawChart(canvas, data) {
  const ctx = canvas.getContext('2d')
  const dpr = window.devicePixelRatio || 1
  const w = canvas.clientWidth
  const h = canvas.clientHeight

  canvas.width = w * dpr
  canvas.height = h * dpr
  ctx.scale(dpr, dpr)

  // Filter out null elevations for drawing
  const valid = data.filter((d) => d.elevation !== null)
  if (valid.length < 2) {
    ctx.fillStyle = '#8a8778'
    ctx.font = '13px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('Insufficient elevation data', w / 2, h / 2)
    return
  }

  // Padding
  const padL = 52
  const padR = 16
  const padT = 16
  const padB = 28

  const plotW = w - padL - padR
  const plotH = h - padT - padB

  const maxDist = data[data.length - 1].distance
  const elevations = valid.map((d) => d.elevation)
  const minElev = Math.min(...elevations)
  const maxElev = Math.max(...elevations)
  const elevRange = maxElev - minElev || 1

  // Scale helpers
  const xScale = (dist) => padL + (dist / maxDist) * plotW
  const yScale = (elev) => padT + plotH - ((elev - minElev) / elevRange) * plotH

  // Background
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(0, 0, w, h)

  // Grid lines
  ctx.strokeStyle = '#333'
  ctx.lineWidth = 0.5
  const gridCountY = 4
  for (let i = 0; i <= gridCountY; i++) {
    const y = padT + (i / gridCountY) * plotH
    ctx.beginPath()
    ctx.moveTo(padL, y)
    ctx.lineTo(padL + plotW, y)
    ctx.stroke()

    // Y-axis labels
    const elev = maxElev - (i / gridCountY) * elevRange
    ctx.fillStyle = '#8a8778'
    ctx.font = '10px monospace'
    ctx.textAlign = 'right'
    ctx.fillText(`${Math.round(elev)}m`, padL - 6, y + 3)
  }

  const gridCountX = 5
  for (let i = 0; i <= gridCountX; i++) {
    const x = padL + (i / gridCountX) * plotW
    ctx.beginPath()
    ctx.moveTo(x, padT)
    ctx.lineTo(x, padT + plotH)
    ctx.stroke()

    // X-axis labels
    const dist = (i / gridCountX) * maxDist / 1000
    ctx.fillStyle = '#8a8778'
    ctx.font = '10px monospace'
    ctx.textAlign = 'center'
    ctx.fillText(`${dist.toFixed(1)}km`, x, padT + plotH + 16)
  }

  // Elevation line
  ctx.strokeStyle = '#c8a415'
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.beginPath()

  let started = false
  for (const pt of data) {
    if (pt.elevation === null) continue
    const x = xScale(pt.distance)
    const y = yScale(pt.elevation)
    if (!started) {
      ctx.moveTo(x, y)
      started = true
    } else {
      ctx.lineTo(x, y)
    }
  }
  ctx.stroke()

  // Fill under curve with semi-transparent amber
  if (started) {
    const lastValid = [...data].reverse().find((d) => d.elevation !== null)
    const firstValid = data.find((d) => d.elevation !== null)
    ctx.lineTo(xScale(lastValid.distance), padT + plotH)
    ctx.lineTo(xScale(firstValid.distance), padT + plotH)
    ctx.closePath()
    ctx.fillStyle = 'rgba(200, 164, 21, 0.10)'
    ctx.fill()
  }
}

/**
 * Compute summary statistics from profile data.
 *
 * @param {{distance: number, elevation: number|null}[]} data
 * @returns {{minElev: number, maxElev: number, totalDist: number, gain: number, loss: number}}
 */
function computeStats(data) {
  const valid = data.filter((d) => d.elevation !== null)
  if (valid.length === 0) {
    return { minElev: 0, maxElev: 0, totalDist: 0, gain: 0, loss: 0 }
  }

  const elevations = valid.map((d) => d.elevation)
  let gain = 0
  let loss = 0
  for (let i = 1; i < valid.length; i++) {
    const diff = valid[i].elevation - valid[i - 1].elevation
    if (diff > 0) gain += diff
    else loss += Math.abs(diff)
  }

  return {
    minElev: Math.min(...elevations),
    maxElev: Math.max(...elevations),
    totalDist: data[data.length - 1].distance,
    gain,
    loss,
  }
}

/**
 * ElevationProfile component.
 *
 * @param {Object} props
 * @param {{lat: number, lng: number}[]|null} props.points - Two-element array or null
 * @param {function} props.onClose - Callback to dismiss the panel
 */
export default function ElevationProfile({ points, onClose }) {
  const [profileData, setProfileData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const canvasRef = useRef(null)

  // Fetch elevation data when points change
  useEffect(() => {
    if (!points || points.length < 2) {
      setProfileData(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetchElevationProfile(points[0], points[1], 50)
      .then((data) => {
        if (cancelled) return
        const allNull = data.every((d) => d.elevation === null)
        if (allNull) {
          setError('No elevation data available for this path')
          setProfileData(null)
        } else {
          setProfileData(data)
        }
      })
      .catch((err) => {
        if (cancelled) return
        setError(err.message || 'Failed to fetch elevation data')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [points])

  // Draw chart when data is ready
  const drawOnCanvas = useCallback(() => {
    if (canvasRef.current && profileData) {
      drawChart(canvasRef.current, profileData)
    }
  }, [profileData])

  useEffect(() => {
    drawOnCanvas()
    window.addEventListener('resize', drawOnCanvas)
    return () => window.removeEventListener('resize', drawOnCanvas)
  }, [drawOnCanvas])

  if (!points || points.length < 2) return null

  const stats = profileData ? computeStats(profileData) : null

  return (
    <div className="elevation-profile" data-testid="elevation-profile">
      <button
        className="elevation-close-btn"
        onClick={onClose}
        type="button"
        title="Close elevation profile"
        data-testid="elevation-close-btn"
      >
        ✕
      </button>

      {loading && (
        <div className="elevation-loading" data-testid="elevation-loading">
          Fetching elevation data...
        </div>
      )}

      {error && !loading && (
        <div className="elevation-error" data-testid="elevation-error">
          {error}
        </div>
      )}

      {profileData && !loading && (
        <>
          <canvas
            ref={canvasRef}
            className="elevation-chart"
            data-testid="elevation-chart"
          />
          {stats && (
            <div className="elevation-stats" data-testid="elevation-stats">
              <span>
                Min: <strong>{Math.round(stats.minElev)}m</strong>
              </span>
              <span>
                Max: <strong>{Math.round(stats.maxElev)}m</strong>
              </span>
              <span>
                Dist: <strong>{(stats.totalDist / 1000).toFixed(1)}km</strong>
              </span>
              <span className="elevation-gain">
                ↑ <strong>{Math.round(stats.gain)}m</strong>
              </span>
              <span className="elevation-loss">
                ↓ <strong>{Math.round(stats.loss)}m</strong>
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}
